<template>
  <div v-if="enrollmentId !== null">
    <MemberDetailHeader
      :enrollment="enrollment"
      @disabled="handleMemoDisabled"
    />

    <div class="row align-items-center mb-5">
      <div class="col">
        <ul class="nav nav-tabs nav-overflow header-tabs">
          <li
            v-for="subMenu in subMenus"
            class="nav-item nav-link"
            :class="{'active': subMenu.value === curSubMenu}"
            @click="handleClickSubMenu(subMenu)"
          >
            {{ subMenu.name }}
          </li>
        </ul>
      </div>
    </div>

    <div class="member-detail-table">
      <member-detail-membership-table
        v-if="curSubMenu === 'membership'"
        :enrollment-id="enrollmentId"
      />
      <member-detail-locker-table
        v-else-if="curSubMenu === 'locker'"
        :enrollment-id="enrollmentId"
        :gender="enrollment.member.gender"
        @update="syncEnrollmentData"
      />
      <member-detail-booking-table
        v-else-if="curSubMenu === 'booking'"
        :enrollment-id="enrollmentId"
      />
    </div>
  </div>
</template>

<script>
import MemberDetailHeader from './MemberDetailHeader';
import MemberDetailMembershipTable from './MembershipTable';
import MemberDetailLockerTable from './LockerTable';
import MemberDetailBookingTable from './BookingTable';
import { fiflboxApi } from '@/utils/api';
import { computedGetters } from '@/utils/vuex-helper';

export default {
  name: 'MemberDetail',
  components: {
    MemberDetailBookingTable,
    MemberDetailLockerTable,
    MemberDetailMembershipTable,
    MemberDetailHeader
  },
  async beforeRouteLeave(from, to, next) {
    if (this.loadingDialogVisible && this.loadingDialogType === 'PUT') {
      // block move to backword
      next(false);
      this.$store.commit('setBulkCustomAlertStatus', {
        isVisible: false,
        title: '페이지 이동',
        description:
          '작업 중인 내용이 있습니다. 페이지 이동 시 예상하지 못한 문제가 발생할 수 있으니 잠시 기다려주실 것을 권장 드립니다. 그럼에도 페이지를 이동하시겠습니까?',
        cancelBtnText: '닫기',
        confirmBtnText: '확인',
        confirmBtnHandler: async () => {
          this.$store.commit('setCustomAlertStatus', {
            target: 'isVisible',
            value: false
          });
          this.$store.commit('setLoadingDialogStatus', {
            target: 'isVisible',
            value: false
          });
          window.removeEventListener(
            this.pageHideEventName,
            this.handleBeforeUnload
          );
          await this.handleLoadingDialogHidden();
          next();
        },
        cancelBtnHandler: () => {
          this.$store.commit('setCustomAlertStatus', {
            target: 'isVisible',
            value: false
          });
        }
      });

      this.$store.commit('setCustomAlertStatus', {
        target: 'isVisible',
        value: true
      });
      return;
    }

    if (!this.isMemoDisabled) {
      next(false);
      this.$store.commit('setBulkCustomAlertStatus', {
        isVisible: false,
        title: '페이지 이동',
        description:
          '작성 중인 내용이 있습니다. 저장하지 않고 페이지를 이동하시겠습니까?',
        cancelBtnText: '닫기',
        confirmBtnText: '확인',
        confirmBtnHandler: () => {
          this.$store.commit('setCustomAlertStatus', {
            target: 'isVisible',
            value: false
          });
          this.$store.commit('setLoadingDialogStatus', {
            target: 'isVisible',
            value: false
          });
          window.removeEventListener(
            this.pageHideEventName,
            this.handleBeforeUnload
          );
          next();
        },
        cancelBtnHandler: () => {
          this.$store.commit('setCustomAlertStatus', {
            target: 'isVisible',
            value: false
          });
        }
      });

      this.$store.commit('setCustomAlertStatus', {
        target: 'isVisible',
        value: true
      });
      return;
    }

    next();
    return;
  },
  data() {
    return {
      enrollmentId: null,
      isMemoDisabled: true,
      subMenus: [
        {
          value: 'membership',
          name: '회원권'
        },
        {
          value: 'locker',
          name: '락커'
        },
        {
          value: 'booking',
          name: '수강 내역'
        }
      ],
      curSubMenu: 'membership',
      enrollment: {
        memo: '',
        member: {
          birth: '',
          user: {
            email: '',
            name: '',
            phone: ''
          }
        }
      },
      pageHideEventName: 'pagehide'
    };
  },
  watch: {
    isRegisteredMemberMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    },
    isMemoDisabled: function(status) {
      if (status) {
        window.removeEventListener(
          this.pageHideEventName,
          this.handleBeforeUnload
        );
      } else {
        window.addEventListener(
          this.pageHideEventName,
          this.handleBeforeUnload
        );
      }
    },
    loadingDialogVisible: function(status) {
      if (status && this.loadingDialogType === 'PUT') {
        window.addEventListener(
          this.pageHideEventName,
          this.handleBeforeUnload
        );
      } else {
        window.removeEventListener(
          this.pageHideEventName,
          this.handleBeforeUnload
        );
      }
    }
  },
  computed: {
    ...computedGetters([
      'isRegisteredMemberMenuVisible',
      'loadingDialogVisible',
      'loadingDialogType'
    ])
  },
  created() {
    this.enrollmentId = this.$route.params.id;
    this.syncEnrollmentData();
    const isOnIOS =
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPhone/i);
    this.pageHideEventName = isOnIOS ? 'pagehide' : 'beforeunload';
  },
  methods: {
    handleClickSubMenu(subMenu) {
      this.curSubMenu = subMenu.value;
    },
    async syncEnrollmentData() {
      try {
        let { data: enrollment } = await fiflboxApi.v1.getEnrollmentMemberById(
          this.enrollmentId
        );

        this.enrollment = enrollment;
      } catch (err) {}
    },
    handleBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = '';
      return;
    },
    handleMemoDisabled(status) {
      this.isMemoDisabled = status;
    },
    handleLoadingDialogHidden() {
      this.$store.commit('setLoadingDialogStatus', {
        target: 'type',
        value: ''
      });
      this.$store.commit('setLoadingDialogStatus', {
        target: 'isVisible',
        value: false
      });
    }
  }
};
</script>

<style lang="scss">
.member-detail-table-control {
  display: flex;
  margin-top: 15px;
  min-height: 10px;
}

.BOOKED {
  color: #9b9b9b;
}

.ATTENDANCE {
  color: #4a4a4a;
}

.LATE {
  color: #ffe707;
}

.ABSENCE {
  color: #ff0000;
}

.NOSHOW {
  color: #ff0000;
}

.WAITING {
  color: #d3d3d3;
}
</style>
